import { create } from "@bufbuild/protobuf";
import {
  createConnectQueryKey,
  useInfiniteQuery,
  useQuery,
  UseQueryOptions,
} from "@connectrpc/connect-query";

import {
  GetArtifactDownloadUrlRequest,
  GetArtifactRequest,
  ListArtifactsRequest,
  ListArtifactsRequestSchema,
} from "@gx/ts-contracts/services/ontology/api/artifact_pb";
import {
  getArtifact,
  getArtifactDownloadUrl,
  listArtifacts,
} from "@gx/ts-contracts/services/ontology/api/artifact-ArtifactService_connectquery";
import { PaginationRequestSchema } from "@gx/ts-contracts/shared/pagination_pb";
import { SortingRequestSchema } from "@gx/ts-contracts/shared/sorting_pb";

import { createInfiniteQueryInput, UseInfiniteQueryOptions } from "@/api/types";

// ========== LIST ARTIFACTS ==========

export const createListArtifactsQueryKey = (input: ListArtifactsRequest) => {
  return createConnectQueryKey({
    schema: listArtifacts,
    input,
    cardinality: "finite",
  });
};

export const useListProjectArtifactsQuery = ({
  input,
  options = {},
}: {
  input: ListArtifactsRequest;
  options?: UseQueryOptions<typeof listArtifacts.output>;
}) => {
  return useQuery<typeof listArtifacts.input, typeof listArtifacts.output>(
    listArtifacts,
    input,
    options,
  );
};

// ========== LIST ARTIFACTS INFINITE ==========

export const createListProjectArtifactsInfiniteQueryKey = (input: ListArtifactsRequest) => {
  return createConnectQueryKey({
    schema: listArtifacts,
    input,
    cardinality: "infinite",
  });
};

export const useListProjectArtifactsInfiniteQuery = ({
  input,
  options = {},
}: {
  input: ListArtifactsRequest;
  options?: UseInfiniteQueryOptions<
    typeof listArtifacts.input,
    typeof listArtifacts.output,
    "pagination"
  >;
}) => {
  const pagination = create(PaginationRequestSchema, {
    total: input.pagination?.total ?? 20,
    cursor: input.pagination?.cursor,
  });

  const sorting = create(SortingRequestSchema, {
    sortBy: input.sorting?.sortBy,
    sortOrder: input.sorting?.sortOrder,
  });

  return useInfiniteQuery<typeof listArtifacts.input, typeof listArtifacts.output, "pagination">(
    listArtifacts,
    createInfiniteQueryInput(ListArtifactsRequestSchema, {
      projectId: input.projectId,
      pagination,
      sorting,
    }),
    {
      pageParamKey: "pagination",
      getNextPageParam: (lastPage) => {
        if (!lastPage.pagination?.nextCursor) return undefined;
        return create(PaginationRequestSchema, {
          total: input.pagination?.total ?? 20,
          cursor: lastPage.pagination.nextCursor,
        });
      },
      ...options,
    },
  );
};

// ========== GET ARTIFACT ==========

export const createGetArtifactQueryKey = (input: GetArtifactRequest) => {
  return createConnectQueryKey({
    schema: getArtifact,
    input,
    cardinality: "finite",
  });
};

export const useGetArtifactQuery = ({
  input,
  options = {},
}: {
  input: GetArtifactRequest;
  options?: UseQueryOptions<typeof getArtifact.output>;
}) => {
  return useQuery<typeof getArtifact.input, typeof getArtifact.output>(getArtifact, input, options);
};

// ========== GET ARTIFACT DOWNLOAD URL ==========

export const createGetArtifactDownloadUrlQueryKey = (input: GetArtifactDownloadUrlRequest) => {
  return createConnectQueryKey({
    schema: getArtifactDownloadUrl,
    input,
    cardinality: "finite",
  });
};

export const useGetArtifactDownloadUrlQuery = ({
  input,
  options = {},
}: {
  input: GetArtifactDownloadUrlRequest;
  options?: UseQueryOptions<typeof getArtifactDownloadUrl.output>;
}) => {
  return useQuery<typeof getArtifactDownloadUrl.input, typeof getArtifactDownloadUrl.output>(
    getArtifactDownloadUrl,
    input,
    options,
  );
};
